import { inject } from '@angular/core';
import * as v from 'valibot';

import { vcEnumLowerCase, vcEnumUpperCase, vcTwoLetterCountryCode } from '../../src/utils/valibot/custom-types';
import { newSchema } from '../../src/utils/valibot/schema';
import { ValidationService } from '../../src/utils/valibot/validation.service';

/**
 * This is the type contained within the AuthInfo object
 * that holds the brands an outlet is authorized for.
 */
export enum AuthInfoBrand {
  BMW = 'BMW',
  BMW_I = 'BMW_I',
  MINI = 'MINI',
  BMW_MOTORCYCLE = 'BMW_MOTORRAD',
  ROLLS_ROYCE = 'ROLLS_ROYCE',
  C_1 = 'C1',
  BMW_ZINORO = 'ZINORO',
  HUSQVARNA = 'HUSQVARNA',
  UNKNOWN = 'UNKNOWN',
}

export enum AuthInfoAuthScheme {
  B2D = 'B2D',
  B2E = 'B2E',
  B2I = 'B2I',
}

export enum AuthInfoAuthRealm {
  INTERNET_B2X = 'internetb2x',
  INTRANET_B2X = 'intranetb2x',
  ALPHA = 'alpha',
}

/**
 * This class represents all the authentication information that the AWP is passing on to embedded web components.
 * Technically the information will be passed as an HTML attribute called "auth-info" and
 * the value will be the serialized form of an AuthInfo object.
 */
export const AuthInfoSchema = newSchema(
  'AuthInfoSchema',
  v.object({
    wenOidcAccessToken: v.pipe(v.string(), v.readonly()),
    csslToken: v.pipe(v.string(), v.readonly()),
    distributionPartnerNumber: v.optional(v.pipe(v.string(), v.readonly())),
    outletNumber: v.optional(v.pipe(v.string(), v.readonly())),
    businessNumber: v.optional(v.pipe(v.string(), v.readonly())),
    authScheme: v.optional(v.pipe(vcEnumUpperCase(AuthInfoAuthScheme), v.readonly())),
    authRealm: v.optional(v.pipe(vcEnumLowerCase(AuthInfoAuthRealm), v.readonly())),
    countryCode: v.pipe(vcTwoLetterCountryCode, v.readonly()),
    brands: v.pipe(v.array(vcEnumUpperCase(AuthInfoBrand)), v.readonly()),
    roles: v.pipe(v.array(v.string()), v.readonly()),
  }),
);

export type AuthInfo = v.InferOutput<typeof AuthInfoSchema>;

/**
 * Parses the given JSON string into an AuthInfo object.
 * Additionally, validates the parsed object against the AuthInfo schema.
 * @param value The JSON string to parse and validate, or an AuthInfo object just to validate
 * @param validationService The validation service to use for schema validation
 * @returns The parsed and validated AuthInfo object
 */
export function parseAuthInfo(value: string | AuthInfo, validationService: ValidationService): AuthInfo {
  const parsed = typeof value === 'string' ? (JSON.parse(value) as AuthInfo) : value;
  return validationService.parseWithSchema(AuthInfoSchema, parsed);
}

/**
 * Returns a transformer function that can be used to parse and validate
 * an AuthInfo object from a JSON string.
 * Basically, a convenience wrapper around `parseAuthInfo`.
 *
 * Note that this must be called in an injection context, but this is guaranteed when calling `input`.
 *
 * @example
 * ```typescript
 * readonly authInfo = input(undefined, { transform: authInfoTransformer() });
 * ```
 *
 * @returns A function that takes a JSON string and returns a validated AuthInfo object
 */
export function authInfoTransformer() {
  const validationService = inject(ValidationService);
  return (json: string) => parseAuthInfo(json, validationService);
}
